import type { NotificationPreferencesData } from "./notification";
import type { UserPresenceStatus } from "./presence";
import type { WorkingHoursConfig } from "./workspace";

// Organization roles
export const ORG_ROLE = {
  ADMIN: "organization:admin",
  MEMBER: "organization:member",
} as const;

export type OrgRole = (typeof ORG_ROLE)[keyof typeof ORG_ROLE];

// Workspace roles
export const WORKSPACE_ROLE = {
  ADMIN: "workspace:admin",
  MEMBER: "workspace:member",
  AGENT: "workspace:agent",
} as const;

export type WorkspaceRole =
  (typeof WORKSPACE_ROLE)[keyof typeof WORKSPACE_ROLE];

// @deprecated - Use ORG_ROLE or WORKSPACE_ROLE instead
export const USER_ROLE = {
  ADMIN: "admin",
  MEMBER: "member",
  AGENT: "agent",
} as const;

// @deprecated - Use OrgRole or WorkspaceRole instead
export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export const USER_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  EXPIRED: "expired",
} as const;

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

// Interfaces
export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  imageUrl: string | null;
  externalId: string;
  organizationId: string;
  role: OrgRole;
  status?: UserStatus;
  invitationId?: string; // Present for PENDING and EXPIRED users
  title?: string;
  timezone?: string;
  language?: string;
  location?: string;
  biography?: string;
  workingHours?: WorkingHoursConfig | null;
  updatedAt: Date;
  createdAt: Date;
  deletedAt: null;
  presence: UserPresence;
  workspaces: UserWorkspace[];
};

export type UserPresence = {
  status: UserPresenceStatus;
  lastUpdatedAt: Date;
  userId: string;
  organizationId: string;
};

// User's workspace assignment with role
export type UserWorkspace = {
  id: string;
  name: string;
  role: WorkspaceRole;
};

// User in workspace context (has workspace role instead of org role)
export type WorkspaceUser = Omit<User, "role" | "workspaces"> & {
  role: WorkspaceRole;
  organizationRole: OrgRole;
  invitationId?: string; // Present for PENDING and EXPIRED users
};

export type WorkspaceInvitation = {
  id: string;
  role: WorkspaceRole;
};

export type UserInvitation = {
  firstName: string;
  lastName: string;
  email: string;
  role: OrgRole;
  workspaces: WorkspaceInvitation[];
};

export type UserPreferences = NotificationPreferencesData;
